import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import CartItem from "../../../../components/marketplace/CartItem/CartItem.jsx";
import FormField from "../../../../components/formField/FormField.jsx";
import Dropdown from "../../../../components/Dropdown.jsx";
import GradientButton from "../../../../components/buttons/gradientButton/GradientButton.jsx";
import LoadingSpinner from "../../../../components/LoadingSpinner.js";
import useMarketplaceRequestValidationSchema from "../../../../components/validationsSchemas/useMarketplaceRequestValidationSchema.js";
import { sendMarketplaceRequest } from "../../../../utility/sendEmail.js";
import { loadProductData } from "../../../../utility/getMarketplaceData.js";
import {
  getRequestItems,
  updateItemQuantity,
  removeItemFromRequest,
  clearRequest,
} from "../../../../redux/marketplaceRequest/reducer.js";
import { ReactComponent as LeftArrow } from "../../../../images/left-arrow.svg";
import "./MarketplaceCart.scss";

const MarketplaceCart = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const alert = useAlert();
  const { t } = useTranslation("marketplace");

  const items = useSelector(getRequestItems);
  const userEmail = useSelector((state) => state.userReducer?.users?.email || "");

  const validationSchema = useMarketplaceRequestValidationSchema();

  const [form, setForm] = useState({
    companyName: "",
    contactFirstName: "",
    contactLastName: "",
    phone: "",
    email: userEmail,
    eventAddress: "",
    invoiceAddress: "",
    eventType: "concert",
    startDate: "",
    endDate: "",
    request: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [products, setProducts] = useState([]);
  const isCartEmpty = items.length === 0;

  const emailInitialized = useRef(false);
  useEffect(() => {
    if (userEmail && !emailInitialized.current) {
      emailInitialized.current = true;
      setForm((prev) => ({ ...prev, email: userEmail }));
    }
  }, [userEmail]);

  const previousItemsRef = useRef([]);

  useEffect(() => {
    const loadProducts = async () => {
      if (items.length === 0) {
        setIsLoadingData(false);
        setProducts([]);
        previousItemsRef.current = [];
        return;
      }

      const currentIds = items.map((item) => item.id).sort();
      const previousIds = previousItemsRef.current.map((item) => item.id).sort();

      const idsChanged = JSON.stringify(currentIds) !== JSON.stringify(previousIds);

      if (!idsChanged) {
        setProducts((prev) =>
          prev.map((product) => {
            const updatedItem = items.find((item) => item.id === product.id);
            return updatedItem ? { ...product, quantity: updatedItem.quantity } : product;
          }),
        );
        return;
      }

      try {
        setIsLoadingData(true);
        const enriched = await loadProductData(items);
        setProducts(enriched);
      } catch (error) {
        console.error("Error enriching items:", error);
        setProducts(items);
      } finally {
        setIsLoadingData(false);
      }

      previousItemsRef.current = items;
    };

    loadProducts();
  }, [items]);

  const handleFieldChange = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleQuantityChange = (itemId, newQty) => {
    dispatch(updateItemQuantity({ itemId, quantity: newQty }));
  };

  const handleRemoveItem = (itemId) => {
    dispatch(removeItemFromRequest({ itemId }));
  };

  const handleSubmit = async () => {
    if (isCartEmpty) {
      alert.show(t("selection.form.empty_cart_message"), { type: "error" });
      return;
    }

    try {
      setIsSubmitting(true);
      await validationSchema.validate(form, { abortEarly: false });

      await sendMarketplaceRequest(form, products);

      alert.show(t("selection.alert_success"), { type: "success" });
      dispatch(clearRequest());
      navigate("/dashboard/marketplace");
    } catch (err) {
      const fieldErrors = {};
      if (err?.inner?.length) {
        err.inner.forEach((e) => {
          fieldErrors[e.path] = e.message;
        });
        setErrors(fieldErrors);
        alert.show(t("selection.alert_error"), { type: "error" });
      } else if (err?.message) {
        alert.show(
          t("selection.email_error", {
            defaultValue: "Une erreur est survenue lors de l'envoi de l'email",
          }),
          { type: "error" },
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const eventTypeOptions = [
    { value: "concert", label: t("event_types.concert") },
    { value: "festival", label: t("event_types.festival") },
    { value: "mariage", label: t("event_types.mariage") },
    { value: "autre", label: t("event_types.autre") },
  ];

  const totalQuantity = products.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);
  const providerNames = [...new Set(products.map((item) => item.providerName).filter(Boolean))];

  return (
    <div className="container-fluid marketplace-selection">
      {isSubmitting && (
        <div className="marketplace-selection__overlay">
          <LoadingSpinner width="60px" />
        </div>
      )}
      <div className="card themeCard">
        <div className="card-body p-sm-3">
          <div className="marketplace-selection__header">
            <button className="marketplace-selection__back-btn" onClick={handleBack} type="button">
              <LeftArrow />
            </button>
            <div>
              <span className="marketplace-selection__eyebrow">
                {t("selection.eyebrow", { defaultValue: "Demande marketplace" })}
              </span>
              <h3 className="marketplace-selection__header-title">{t("selection.title")}</h3>
              <p className="marketplace-selection__header-subtitle">
                {t("selection.subtitle", {
                  defaultValue:
                    "Vérifiez votre sélection et envoyez vos informations pour être recontacté.",
                })}
              </p>
            </div>
          </div>

          <div className="marketplace-selection__layout">
            <div className="marketplace-selection__main">
              <section className="marketplace-selection__panel">
                <div className="marketplace-selection__panel-header">
                  <span>1</span>
                  <div>
                    <h4>{t("selection.products_title", { defaultValue: "Votre sélection" })}</h4>
                    <p>
                      {t("selection.products_subtitle", {
                        defaultValue: "Ajoutez ou ajustez les produits avant d’envoyer la demande.",
                      })}
                    </p>
                  </div>
                </div>

                <div className="marketplace-selection__products">
                  {isLoadingData && items.length > 0 ? (
                    <div className="marketplace-selection__loading">
                      <LoadingSpinner width="50px" />
                    </div>
                  ) : products.length > 0 ? (
                    products.map((item) => (
                      <CartItem
                        key={item.id}
                        item={item}
                        quantity={item.quantity}
                        onQuantityChange={handleQuantityChange}
                        onRemove={handleRemoveItem}
                      />
                    ))
                  ) : (
                    <div className="marketplace-selection__empty">
                      <h4>{t("selection.empty")}</h4>
                      <p>
                        {t("selection.empty_hint", {
                          defaultValue:
                            "Retournez dans la marketplace pour sélectionner un fournisseur ou un produit.",
                        })}
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate("/dashboard/marketplace")}
                      >
                        {t("selection.browse_marketplace", {
                          defaultValue: "Parcourir la marketplace",
                        })}
                      </button>
                    </div>
                  )}
                </div>
              </section>

              <section className="marketplace-selection__panel marketplace-selection__form">
                <div className="marketplace-selection__panel-header">
                  <span>2</span>
                  <div>
                    <h4 className="marketplace-selection__form-title">
                      {t("selection.form.title")}
                    </h4>
                    <p>
                      {t("selection.form.subtitle", {
                        defaultValue:
                          "Ces informations permettent au fournisseur de préparer une réponse pertinente.",
                      })}
                    </p>
                  </div>
                </div>

              <div className="marketplace-selection__form-grid">
                <div className="marketplace-selection__form-full">
                  <FormField
                    label={t("selection.form.company_name")}
                    type="text"
                    value={form.companyName}
                    onChange={handleFieldChange("companyName")}
                    placeholder={t("selection.form.company_name_placeholder")}
                    error={errors.companyName}
                    secondaryTheme
                  />
                </div>

                <div className="marketplace-selection__form-half">
                  <FormField
                    label={t("selection.form.contact_first_name")}
                    type="text"
                    value={form.contactFirstName}
                    onChange={handleFieldChange("contactFirstName")}
                    placeholder={t("selection.form.contact_first_name_placeholder")}
                    error={errors.contactFirstName}
                    secondaryTheme
                  />
                </div>

                <div className="marketplace-selection__form-half">
                  <FormField
                    label={t("selection.form.contact_last_name")}
                    type="text"
                    value={form.contactLastName}
                    onChange={handleFieldChange("contactLastName")}
                    placeholder={t("selection.form.contact_last_name_placeholder")}
                    error={errors.contactLastName}
                    secondaryTheme
                  />
                </div>

                <div className="marketplace-selection__form-full">
                  <FormField
                    label={t("selection.form.email")}
                    type="email"
                    value={form.email}
                    onChange={handleFieldChange("email")}
                    placeholder="company@mail.com"
                    error={errors.email}
                    secondaryTheme
                  />
                </div>

                <div className="marketplace-selection__form-half">
                  <FormField
                    label={t("selection.form.phone")}
                    type="tel"
                    value={form.phone}
                    onChange={handleFieldChange("phone")}
                    error={errors.phone}
                    secondaryTheme
                  />
                </div>

                <div className="marketplace-selection__form-half">
                  <div className="fieldGroup secondaryTheme">
                    <label className="form-label __form-label">
                      {t("selection.form.event_type")}
                    </label>
                    <Dropdown
                      options={eventTypeOptions}
                      value={eventTypeOptions.find((opt) => opt.value === form.eventType)}
                      onChange={(selected) => {
                        handleFieldChange("eventType")(selected.value);
                      }}
                    />
                  </div>
                  {errors.eventType && <p className="form-error">{errors.eventType}</p>}
                </div>

                <div className="marketplace-selection__form-half">
                  <FormField
                    label={t("selection.form.event_address")}
                    type="text"
                    value={form.eventAddress}
                    onChange={handleFieldChange("eventAddress")}
                    placeholder={t("selection.form.event_address_placeholder")}
                    error={errors.eventAddress}
                    secondaryTheme
                  />
                </div>

                <div className="marketplace-selection__form-half">
                  <FormField
                    label={t("selection.form.invoice_address")}
                    type="text"
                    value={form.invoiceAddress}
                    onChange={handleFieldChange("invoiceAddress")}
                    placeholder={t("selection.form.invoice_address_placeholder")}
                    error={errors.invoiceAddress}
                    secondaryTheme
                  />
                </div>

                <div className="marketplace-selection__form-label">
                  {t("selection.form.event_duration")}
                </div>

                <div className="marketplace-selection__form-half">
                  <FormField
                    type="date"
                    value={form.startDate}
                    onChange={handleFieldChange("startDate")}
                    error={errors.startDate}
                    secondaryTheme
                  />
                </div>

                <div className="marketplace-selection__form-half">
                  <FormField
                    type="date"
                    value={form.endDate}
                    onChange={handleFieldChange("endDate")}
                    error={errors.endDate}
                    secondaryTheme
                  />
                </div>

                <div className="marketplace-selection__form-full">
                  <FormField
                    label={t("selection.form.request_optional")}
                    type="longText"
                    value={form.request}
                    onChange={handleFieldChange("request")}
                    placeholder={t("selection.form.request_placeholder")}
                    secondaryTheme
                  />
                </div>
              </div>
              </section>
            </div>

            <aside className="marketplace-selection__summary">
              <h4>{t("selection.summary_title", { defaultValue: "Résumé" })}</h4>
              <div className="marketplace-selection__summary-stats">
                <div>
                  <strong>{totalQuantity}</strong>
                  <span>{t("selection.summary_items", { defaultValue: "article(s)" })}</span>
                </div>
                <div>
                  <strong>{providerNames.length}</strong>
                  <span>
                    {t("selection.summary_providers", { defaultValue: "fournisseur(s)" })}
                  </span>
                </div>
              </div>
              {providerNames.length > 0 && (
                <div className="marketplace-selection__summary-providers">
                  <span>{t("selection.summary_provider_list", { defaultValue: "Fournisseurs" })}</span>
                  {providerNames.map((providerName) => (
                    <p key={providerName}>{providerName}</p>
                  ))}
                </div>
              )}
              {isCartEmpty && (
                <p className="marketplace-selection__submit-message">
                  {t("selection.form.empty_cart_message")}
                </p>
              )}
              <GradientButton onPress={handleSubmit} disabled={isCartEmpty || isSubmitting}>
                {isSubmitting
                  ? t("selection.form.submitting", { defaultValue: "Envoi en cours..." })
                  : t("selection.form.submit")}
              </GradientButton>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceCart;
